/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';

import {
  BuilderNavigationService,
  BuilderUserContext,
  FilterNames,
  FilterRestService,
} from '@gh/builder-shared';
import { BuilderPermission, JobStatus, SystemRoleBit } from '@gh/builder-shared-data';
import {
  AppRouter, DateTimeTypes,
  filterableForm,
  FilterableFormState,
  FormStateBuilder,
  getRouteInitData,
  NumberTypes,
} from '@gh/common-shared';
import { filterTracker, RouteDataService, SlicedDataSource } from '@gh/core-data';
import { DialogService, NotificationService } from '@gh/core-ui';
import {
  buildDateTimeColumn, buildNumberColumnLeft,
  cursorTracker,
  GridDefinitionFactory,
  LayoutBasedGridDefinition,
  newGridPaginationCursor,
} from '@gh/core-ui-grid';

import { JobListFilter, JobListItem } from '../../resources/job.model';
import { JobOrderListBusinessService } from '../../resources/job-order-list-business.service';
import { JobNavigationService } from '../../resources/job-navigation.service';
import { GridSettingsService } from '@gh/common-internal-shared';

@Component({
  standalone: false,
  selector: 'gh-job-order-list',
  templateUrl: 'job-order-list.component.html',
})
export class JobOrderListComponent implements OnInit, OnDestroy {
  dataSource: SlicedDataSource<JobListItem>;
  form: FilterableFormState<JobListFilter>;
  gridDefinition: LayoutBasedGridDefinition;
  cursor = newGridPaginationCursor();

  get havePermissions(): boolean {
    return this.userContext.hasSystemRole(SystemRoleBit.Corporate)
      || this.userContext.hasSystemRole(SystemRoleBit.Master)
      || this.userContext.hasPermission(BuilderPermission.JobAdmin.JobSpecifications.CREATE_EDIT)
      || this.userContext.hasPermission(BuilderPermission.JobAdmin.JobSpecifications.VIEW);
  }

  constructor(private route: ActivatedRoute,
              private routeData: RouteDataService,
              private userContext: BuilderUserContext,
              private formStateBuilder: FormStateBuilder,
              private businessService: JobOrderListBusinessService,
              private dialogs: DialogService,
              private jobNavigation: JobNavigationService,
              private notifications: NotificationService,
              private router: AppRouter,
              private filterService: FilterRestService,
              private navigationService: BuilderNavigationService,
              private gridService: GridSettingsService,
              public gridDefinitionFactory: GridDefinitionFactory) {
    this.gridService.setCurrentTable('jobOrdersList');

  }

  get canCreateQuickJob(): boolean {
    return this.userContext.hasPermission(BuilderPermission.JobAdmin.Jobs.CREATE_QUICK_JOB);
  }

  isJobDpp(job: JobListItem): boolean {
    return job.isDpp;
  }

  ngOnInit(): void {
    const { filter: initFilter, gridLayout } = getRouteInitData(this.route);
    let isInit = true;
    this.gridDefinition = this.gridDefinitionFactory.buildLayoutBased(
      {
        originalBudget: buildNumberColumnLeft(NumberTypes.Price),
        originalContractAmount: buildNumberColumnLeft(NumberTypes.Price),
        originalProfitMargin: buildNumberColumnLeft(NumberTypes.Price),
        currentBudget: buildNumberColumnLeft(NumberTypes.Price),
        status: {
          minWidth: 120,
          cellClass: ({ data }: { data: any }) => ({
            [JobStatus.Dpp]: 'cell--important',
            [JobStatus.NotProceeding]: 'cell--negative',
            [JobStatus.Active]: 'cell--positive',
            [JobStatus.Finalized]: 'finalized-status',
          }[data.statusValue]),
        },
        authToCommenceDate: buildDateTimeColumn(DateTimeTypes.ShortDate),
        councilApprovedDate: buildDateTimeColumn(DateTimeTypes.ShortDate),
      },
      {
        defaultColDef: {
          comparator: () => 0,
        },
        getRowId: (params) => String(params.data.id)
      });
    this.gridDefinition.bind(gridLayout);

    this.form = filterableForm(this.formStateBuilder.buildPlain({
      value: initFilter,
      children: {
        searchString: void 0,
        showFinalized: void 0,
        showDpp: void 0,
        showActive: void 0,
        showUserAssignedJobs: void 0,
        showNotProceeding: void 0,
      },
    }));

    const tracker = this.routeData.tracker(filterTracker(this.form))
      .withEffects(cursorTracker(this.cursor));
    tracker.listen(this.route);
    tracker.filterChanges.subscribe(([filter]) => {
      this.dataSource = this.businessService.findAllAsDataSource({
        ...filter,
      });
      if (!isInit) {
        this.filterService.update(FilterNames.jobOrdersList, filter).subscribe();
      }
      isInit = false;
    });
  }

  ngOnDestroy(): void {
    this.gridService.leaveTable();
  }

  onJobDetailsOpen(job: JobListItem): void {
    this.jobNavigation.onJobDetailsOpen(job, 'lbl_job_orders');
  }

  goToAel(job?: JobListItem): void {
    this.navigationService.goToAel({
      label: 'lbl_job_orders',
      entityType: 'JOB',
      entityId: job ? job.id : undefined,
    });
  }

  onSearch(): void {
    this.form.emitFilter();
  }

  onJobSpecificationOpen(job: JobListItem): void {
    this.jobNavigation.onJobSpecificationOpen(job, 'lbl_job_orders');
  }

  onColorSelectionOpen(job: JobListItem): void {
    this.jobNavigation.onColorSelectionOpen(job, 'lbl_job_orders');
  }

  onJobEstimateOpen(job: JobListItem): void {
    this.jobNavigation.onJobEstimateOpen(job);
  }

  onFinancialsOpen(job: JobListItem): void {
    this.jobNavigation.onFinancialsOpen(job);
  }

  onJobScheduleOpen(job: JobListItem): void {
    // not implemented yet
  }

  onJobOrderOpen(job: JobListItem): void {
    this.jobNavigation.onJobOrderOpen(job, 'lbl_job_orders');
  }

  onJobChangeOrderOpen(job: JobListItem): void {
    this.jobNavigation.onJobChangeOrderOpen(job, 'lbl_job_orders');
  }
}
